/**
 * CR-6435 / CR-6436: draft / applied state and option loading for the
 * Calendar filter panel, in both portal views.
 *
 * Draft edits never touch the events request; Apply filters copies the draft
 * to the applied state, whose query params useCalendar adds to the events
 * request. The active-filter badge counts the draft, so it reacts as soon as
 * a value is picked. Every cascade is applied on the draft as the user edits
 * it (see calendarFilterParams).
 *
 * Option sources differ by view. The customer view reads the same scoped
 * endpoints the Dashboard and Deliveries panels use. The supplier view's
 * Company section is the CUSTOMERS the supplier serves (the Dashboard's
 * `customer_filter` context) and its Supplier section is the supplier's own
 * sites — sister sites at GROUP view, else the active site alone — from the
 * same endpoint the Deliveries supplier layout reads.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { coreAPI, purchasingAPI } from '../../lib/api';
import type { Company, CommodityGroup, CommodityType, Site } from '../../lib/api';
import { useIsCustomer, useIsSupplier } from '../../hooks/usePortalMode';
import { useViewKey } from '../../contexts/ViewContext';
import { deriveCountryOptions } from '../../components/dashboard/dashboardFilterUtils';
import {
  type CalendarFiltersState,
  type CalendarSiteOption,
  type CalendarSupplierOption,
  type CalendarCommodityTypeOption,
  EMPTY_CALENDAR_FILTERS,
  calendarFilterQueryParams,
  countActiveCalendarFilters,
  commodityTypesForSelection,
  deriveDivisions,
  reconcileCalendarFilters,
  sitesForSelection,
  suppliersForSelection,
} from './calendarFilterParams';

interface Option { value: string; label: string }

/** Which panel the hook serves; null when the user is in neither portal. */
export type CalendarFilterMode = 'customer' | 'supplier' | null;

export interface CalendarFilterOptions {
  divisions: Option[];
  companyCountries: string[];
  sites: CalendarSiteOption[];
  supplierGroups: Option[];
  supplierCountries: string[];
  suppliers: CalendarSupplierOption[];
  commodityGroups: Option[];
  commodityTypes: CalendarCommodityTypeOption[];
}

interface RawOptions {
  sites: CalendarSiteOption[];
  siteGroups: Array<{ group: number | null; groupName?: string | null }>;
  supplierGroups: Option[];
  suppliers: CalendarSupplierOption[];
  commodityGroups: Option[];
  commodityTypes: CalendarCommodityTypeOption[];
}

const EMPTY_RAW: RawOptions = {
  sites: [], siteGroups: [], supplierGroups: [], suppliers: [], commodityGroups: [], commodityTypes: [],
};

function unwrap<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  const results = (data as { results?: unknown } | null)?.results;
  return Array.isArray(results) ? (results as T[]) : [];
}

const toSiteOptions = (sites: Site[]): CalendarSiteOption[] => sites.map((s) => ({
  value: String(s.id), label: s.name, group: s.group_id ?? null, country: s.country || '',
}));

const toSiteGroups = (sites: Site[]) => sites.map((s) => ({ group: s.group_id ?? null, groupName: s.group_name }));

const toSupplierOptions = (companies: Company[]): CalendarSupplierOption[] => companies.map((c) => ({
  value: String(c.id),
  label: c.name,
  group: c.group ?? null,
  country: c.country || '',
  customers: Array.isArray(c.customers) ? c.customers : null,
}));

async function loadCustomerOptions(): Promise<RawOptions> {
  const [sitesRes, groupsRes, suppliersRes, cgRes, ctRes] = await Promise.all([
    coreAPI.getAccessibleSites().catch(() => ({ data: [] as Site[] })),
    coreAPI.getSupplierGroupsForFilter().catch(() => ({ data: { results: [] } })),
    coreAPI.getSupplierSitesForFilter().catch(() => ({ data: { results: [] as Company[] } })),
    purchasingAPI.getCommodityGroups({ enabled: true }).catch(() => ({ data: [] as CommodityGroup[] })),
    purchasingAPI.getCommodityTypes({ enabled: true }).catch(() => ({ data: [] as CommodityType[] })),
  ]);
  const sites = unwrap<Site>(sitesRes.data);
  const groups = unwrap<{ id: number; name: string }>(groupsRes.data);
  const cgs = unwrap<CommodityGroup>(cgRes.data).filter((g) => g.enabled);
  const cts = unwrap<CommodityType>(ctRes.data).filter((t) => t.enabled);
  return {
    sites: toSiteOptions(sites),
    siteGroups: toSiteGroups(sites),
    supplierGroups: groups.map((g) => ({ value: String(g.id), label: g.name })),
    suppliers: toSupplierOptions(unwrap<Company>(suppliersRes.data)),
    commodityGroups: cgs.map((g) => ({ value: String(g.id), label: g.name })),
    commodityTypes: cts.map((t) => ({ value: String(t.id), label: t.name, group: t.commodity_group ?? null })),
  };
}

async function loadSupplierOptions(): Promise<RawOptions> {
  const [sitesRes, suppliersRes] = await Promise.all([
    coreAPI.getAccessibleSites({ context: 'customer_filter' }).catch(() => ({ data: [] as Site[] })),
    coreAPI.getSupplierSitesForFilter().catch(() => ({ data: { results: [] as Company[] } })),
  ]);
  const sites = unwrap<Site>(sitesRes.data);
  return {
    ...EMPTY_RAW,
    sites: toSiteOptions(sites),
    siteGroups: toSiteGroups(sites),
    suppliers: toSupplierOptions(unwrap<Company>(suppliersRes.data)),
  };
}

/**
 * Filter state + options for the Calendar panel.
 *
 * @returns the panel mode, draft / applied state, field setter with
 *   cascades, apply / clear, the active count, the applied query params, and
 *   the cascaded options.
 */
export function useCalendarFilters() {
  const isCustomer = useIsCustomer();
  const isSupplier = useIsSupplier();
  const mode: CalendarFilterMode = isCustomer ? 'customer' : isSupplier ? 'supplier' : null;
  const viewKey = useViewKey(); // supplier options follow the site switcher
  const [raw, setRaw] = useState<RawOptions>(EMPTY_RAW);
  const [draft, setDraft] = useState<CalendarFiltersState>(EMPTY_CALENDAR_FILTERS);
  const [applied, setApplied] = useState<CalendarFiltersState>(EMPTY_CALENDAR_FILTERS);

  useEffect(() => {
    if (!mode) return;
    let cancelled = false;
    const load = mode === 'customer' ? loadCustomerOptions : loadSupplierOptions;
    load().then((next) => { if (!cancelled) setRaw(next); });
    return () => { cancelled = true; };
  }, [mode, viewKey]);

  const setField = useCallback(<K extends keyof CalendarFiltersState>(key: K, value: CalendarFiltersState[K]) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'commodityGroup') next.commodityType = '';
      return reconcileCalendarFilters(next, raw.sites, raw.suppliers, raw.commodityTypes);
    });
  }, [raw.sites, raw.suppliers, raw.commodityTypes]);

  const apply = useCallback(() => setApplied(draft), [draft]);
  const clear = useCallback(() => {
    setDraft(EMPTY_CALENDAR_FILTERS);
    setApplied(EMPTY_CALENDAR_FILTERS);
  }, []);

  // The request follows the APPLIED state; the badge follows the DRAFT, so it
  // updates the moment a value is picked (CR-6449, as the ASN / Forecast
  // panels do) while the events request still waits for Apply filters.
  const queryParams = useMemo(() => calendarFilterQueryParams(applied), [applied]);
  const activeCount = useMemo(() => countActiveCalendarFilters(draft), [draft]);

  const options: CalendarFilterOptions = useMemo(() => ({
    divisions: deriveDivisions(raw.siteGroups),
    companyCountries: deriveCountryOptions(raw.sites),
    sites: sitesForSelection(raw.sites, draft),
    supplierGroups: raw.supplierGroups,
    supplierCountries: deriveCountryOptions(raw.suppliers),
    suppliers: suppliersForSelection(raw.suppliers, draft),
    commodityGroups: raw.commodityGroups,
    commodityTypes: commodityTypesForSelection(raw.commodityTypes, draft),
  }), [raw, draft]);

  return { mode, draft, applied, setField, apply, clear, queryParams, activeCount, options };
}

export type CalendarFiltersApi = ReturnType<typeof useCalendarFilters>;
